import { demoFunc } from './dees-harness-session-sidebar.demo.js';
import {
  DeesElement,
  html,
  css,
  customElement,
  type TemplateResult,
  property,
  state,
  cssManager,
  directives,
} from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';
import { harnessControlStyles, harnessSidebarStyles } from '../harness.styles.js';
import type {
  IHarnessIntelligenceExchange,
  IHarnessMessage,
  IHarnessScratchpad,
  IHarnessScratchpadSaveDetail,
  IHarnessSessionIntelligenceAskDetail,
  IHarnessSessionMetrics,
  IHarnessSubtaskOpenDetail,
  IHarnessTodoItem,
  IHarnessToolCall,
  THarnessIntelligenceAvailabilityStatus,
  THarnessToolStatus,
} from '../interfaces.js';
import {
  harnessEffectiveToolStatus,
  harnessFormatRelativeTime,
  harnessToolStatusPresentation,
} from '../harness.helpers.js';
import { DeesHarnessToolRegistry } from '../toolregistry.js';
import { DeesHarnessToolFullscreen } from '../dees-harness-tool-card/dees-harness-tool-fullscreen.js';
import '../dees-harness-todos/dees-harness-todos.js';
import '../dees-harness-overflow-text.js';
import '../../00group-button/dees-button/dees-button.js';
import '../../00group-utility/dees-icon/dees-icon.js';

const { repeat } = directives;

const formatTokenCount = (valueArg: number): string => valueArg.toLocaleString('en-US');
export type THarnessSessionSidebarSection =
  'tasks' | 'activity' | 'metrics' | 'scratchpad' | 'intelligence';

interface IToolActivityEntry {
  key: string;
  messageId: string;
  call: IHarnessToolCall;
  createdAt?: number;
  status: THarnessToolStatus;
}

declare global {
  interface HTMLElementTagNameMap {
    'dees-harness-session-sidebar': DeesHarnessSessionSidebar;
  }
}

/**
 * Generic session context sidebar. It renders host-owned state and emits
 * mutations, but never loads, persists, or executes session data itself.
 */
@customElement('dees-harness-session-sidebar')
export class DeesHarnessSessionSidebar extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Agent Chat'];

  @property({ attribute: false })
  accessor todos: IHarnessTodoItem[] = [];

  @property({ type: Boolean })
  accessor showTasks: boolean = true;

  /** Top-level tool messages from the host's currently retained transcript window. */
  @property({ attribute: false })
  accessor toolMessages: IHarnessMessage[] = [];

  @property({ attribute: false })
  accessor toolRegistry: DeesHarnessToolRegistry = DeesHarnessToolRegistry.default;

  @property({ attribute: false })
  accessor metrics: IHarnessSessionMetrics = {};

  @property({ attribute: false })
  accessor scratchpad: IHarnessScratchpad | undefined = undefined;

  @property({ type: Boolean })
  accessor scratchpadBusy: boolean = false;

  @property()
  accessor scratchpadError: string = '';

  @property({ type: Boolean })
  accessor intelligenceEnabled: boolean = false;

  @property()
  accessor intelligenceAvailabilityStatus: THarnessIntelligenceAvailabilityStatus = 'available';

  @property()
  accessor intelligenceUnavailableReason: string = '';

  @property()
  accessor intelligenceHeading: string = 'Session Intelligence';

  @property({ attribute: false })
  accessor intelligenceExchanges: IHarnessIntelligenceExchange[] = [];

  @property({ type: Boolean })
  accessor intelligenceBusy: boolean = false;

  @property()
  accessor intelligenceError: string = '';

  /** Stable host session identity used to scope local editor drafts. */
  @property()
  accessor sessionKey: string = '';

  @property({ type: Boolean })
  accessor disabled: boolean = false;

  @state()
  private accessor scratchpadDraft: string = '';

  @state()
  private accessor scratchpadDraftRevision: number = 0;

  @state()
  private accessor scratchpadDirty: boolean = false;

  @state()
  private accessor scratchpadStale: boolean = false;

  @state()
  private accessor intelligenceQuestion: string = '';

  @state()
  private accessor expandedSection: THarnessSessionSidebarSection | undefined = undefined;

  private sectionStateInitialized = false;
  private readonly activityPositions = new Map<string, number>();
  private activityFlipReady = false;
  private toolInspectorPortal: DeesHarnessToolFullscreen | undefined;
  private inspectedToolKey: string | undefined;
  private inspectedSessionKey: string | undefined;
  private toolInspectorTrigger: HTMLElement | undefined;

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    harnessControlStyles,
    harnessSidebarStyles,
    css`
      .sectionContent dees-harness-todos {
        min-width: 0;
      }

      .scratchpadSection .sectionContent:not([hidden]) {
        display: flex;
        flex-direction: column;
        overflow: hidden;
        padding: 0;
      }

      .scratchpadSection textarea {
        flex: 1;
        min-height: 0;
        resize: none;
        overflow-y: auto;
        padding: var(--dees-spacing-sm) var(--dees-spacing-md);
        border: 0;
        border-radius: 0;
        background: color-mix(in srgb, var(--dees-color-bg-secondary) 92%, black);
        color: var(--dees-color-text-secondary);
      }

      .scratchpadSection textarea:focus-visible {
        border-color: transparent;
        background: color-mix(in srgb, var(--dees-color-bg-secondary) 92%, black);
        box-shadow: none;
      }

      .scratchpadSection .conflict,
      .scratchpadSection .error {
        margin-right: var(--dees-spacing-md);
        margin-left: var(--dees-spacing-md);
      }

      .scratchpadFooter {
        display: flex;
        flex: 0 0 auto;
        align-items: center;
        justify-content: space-between;
        gap: var(--dees-spacing-sm);
        padding: var(--dees-spacing-xs) var(--dees-spacing-md) var(--dees-spacing-sm);
        border-top: 0;
        background: linear-gradient(
          to bottom,
          color-mix(in srgb, var(--dees-color-bg-secondary) 92%, black),
          var(--dees-color-bg-secondary)
        );
      }

      .scratchpadFooter .editorMeta {
        margin-top: 0;
      }

      .intelligenceSection .sectionContent:not([hidden]) {
        display: flex;
        flex-direction: column;
        overflow: hidden;
        padding: 0;
      }

      .intelligenceHistory {
        flex: 1;
        min-height: 0;
        overflow-x: hidden;
        overflow-y: auto;
        padding: var(--dees-spacing-sm) var(--dees-spacing-md);
        scrollbar-width: thin;
        scrollbar-color: var(--dees-color-scrollbar-thumb) transparent;
      }

      .intelligenceComposer {
        flex: 0 0 auto;
        padding: var(--dees-spacing-sm) var(--dees-spacing-md) var(--dees-spacing-md);
        background: linear-gradient(
          to bottom,
          var(--dees-color-bg-primary),
          var(--dees-color-bg-secondary)
        );
        box-shadow: var(--dees-shadow-up-sm);
      }

      .intelligenceComposer textarea {
        min-height: 64px;
        max-height: 160px;
        resize: none;
        padding: var(--dees-spacing-sm) 0;
        border: 0;
        border-radius: 0;
        background: transparent;
        color: var(--dees-color-text-primary);
        font-family: inherit;
        font-size: var(--dees-font-control-size, 13px);
      }

      .intelligenceComposer textarea:focus-visible {
        border-color: transparent;
        box-shadow: none;
        outline: 2px solid color-mix(in srgb, var(--dees-color-accent-primary) 55%, transparent);
        outline-offset: 2px;
      }

      .intelligenceControls {
        display: flex;
        align-items: center;
        gap: var(--dees-spacing-sm);
      }

      .intelligenceControls .hint {
        flex: 1;
        min-width: 0;
      }

      .intelligenceAvailability {
        margin-bottom: var(--dees-spacing-sm);
        padding: var(--dees-spacing-xs) var(--dees-spacing-sm);
        border-radius: var(--dees-radius-md);
        background: var(--dees-color-fill-secondary);
        color: var(--dees-color-text-primary);
        font-size: var(--dees-font-caption1-size, 11px);
        line-height: 1.4;
      }

      .activityList,
      .activityGroup {
        display: grid;
        gap: var(--dees-spacing-sm);
      }

      .activityGroup + .activityGroup {
        margin-top: var(--dees-spacing-md);
      }

      .activityGroupTitle {
        color: var(--dees-color-text-secondary);
        font-size: 11px;
        font-weight: 600;
      }

      /*
       * The list moves rather than jumps: an entry that changes place is put back where it was for
       * one frame and then released, so this transition carries it — from Active down into History
       * as well, because the entry keeps its key. An entry the list has not shown before waits for
       * that travel to finish and only then blends in.
       */
      .activityItem,
      .activityGroupTitle {
        transition: transform var(--dees-transition-default) var(--dees-ease-standard);
      }

      .activityItem[data-activity-entering],
      .activityGroupTitle[data-activity-entering] {
        animation: harness-activity-blend var(--dees-transition-default) var(--dees-ease-standard)
          var(--dees-transition-default) backwards;
      }

      @keyframes harness-activity-blend {
        from {
          opacity: 0;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .activityItem,
        .activityGroupTitle {
          transition: none;
        }

        .activityItem[data-activity-entering],
        .activityGroupTitle[data-activity-entering] {
          animation: none;
        }
      }

      .activityItem {
        appearance: none;
        display: grid;
        grid-template-columns: 24px minmax(0, 1fr);
        gap: var(--dees-spacing-sm);
        align-items: start;
        min-width: 0;
        padding: var(--dees-spacing-xs) 0;
        width: 100%;
        border: 0;
        background: transparent;
        color: inherit;
        font: inherit;
        text-align: left;
        cursor: pointer;
        border-radius: var(--dees-radius-md);
      }

      .activityItem:hover {
        background: var(--dees-color-fill-secondary);
      }

      .activityItem:focus-visible {
        outline: 2px solid var(--dees-color-focus-ring);
        outline-offset: 2px;
      }

      .activityIcon {
        display: grid;
        place-items: center;
        width: 24px;
        height: 24px;
        border-radius: var(--dees-radius-md);
        background: var(--dees-color-fill-tertiary);
        color: var(--dees-color-text-secondary);
      }

      .activityItem.status-running .activityIcon,
      .activityItem.status-pending .activityIcon {
        color: var(--dees-color-accent-primary);
      }

      .activityItem.status-error .activityIcon {
        color: var(--dees-color-accent-error);
      }

      .activityCopy {
        min-width: 0;
      }

      .activityTitleRow,
      .activityMeta {
        display: flex;
        align-items: center;
        gap: var(--dees-spacing-xs);
        min-width: 0;
      }

      .activityTitle {
        min-width: 0;
        flex: 1;
        color: var(--dees-color-text-primary);
        font-size: var(--dees-font-control-size-sm, 12px);
        font-weight: 600;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .activityStatus {
        flex: 0 0 auto;
        color: var(--dees-color-text-muted);
        font-size: var(--dees-font-caption2-size, 10px);
        text-transform: capitalize;
        white-space: nowrap;
      }

      .activityItem.status-running .activityStatus,
      .activityItem.status-pending .activityStatus {
        color: var(--dees-color-accent-primary);
      }

      .activityItem.status-error .activityStatus {
        color: var(--dees-color-text-error);
      }

      .activityExitCode {
        flex: 0 0 auto;
        color: var(--dees-color-text-warning);
        font-size: var(--dees-font-caption2-size, 10px);
        font-weight: 600;
        white-space: nowrap;
      }

      .activityStatusPair {
        display: inline-flex;
        align-items: center;
        gap: var(--dees-spacing-xs);
        flex: 0 0 auto;
        white-space: nowrap;
      }

      .activityInspect {
        display: inline-grid;
        place-items: center;
        color: var(--dees-color-text-muted);
      }

      .activitySubtitle {
        display: block;
        width: 100%;
        min-width: 0;
        margin-top: 1px;
        color: var(--dees-color-text-secondary);
        font-size: var(--dees-font-caption2-size, 10px);
      }

      .activityMeta {
        margin-top: 2px;
        color: var(--dees-color-text-muted);
        font-size: var(--dees-font-caption2-size, 10px);
      }

      .metricList {
        display: grid;
        gap: 6px;
      }

      .metricRow {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: var(--dees-spacing-md);
        color: var(--dees-color-text-secondary);
        font-size: var(--dees-font-caption1-size, 11px);
      }

      .metricValue {
        color: var(--dees-color-text-primary);
        font-family: var(--dees-font-family-mono);
        font-variant-numeric: tabular-nums;
        text-align: right;
      }

      .contextProgress {
        height: 4px;
        margin-top: 2px;
        overflow: hidden;
        border-radius: var(--dees-radius-full);
        background: var(--dees-color-fill-secondary);
      }

      .contextProgressFill {
        height: 100%;
        border-radius: inherit;
        background: var(--dees-color-accent-primary);
        transition: width var(--dees-transition-default) var(--dees-ease-standard);
      }

      textarea {
        box-sizing: border-box;
        width: 100%;
        min-height: 108px;
        resize: vertical;
        padding: var(--dees-spacing-sm);
        border: 1px solid var(--dees-color-border-default);
        border-radius: var(--dees-radius-md);
        background: var(--dees-color-bg-primary);
        color: var(--dees-color-text-primary);
        font: var(--dees-font-caption1-size, 11px) / 1.5 var(--dees-font-family-mono);
        outline: none;
      }

      textarea:focus-visible {
        border-color: var(--dees-color-accent-primary);
        box-shadow: 0 0 0 1px var(--dees-color-accent-primary);
      }

      textarea:disabled {
        opacity: 0.62;
        cursor: not-allowed;
      }

      .editorMeta,
      .exchangeMeta,
      .hint {
        color: var(--dees-color-text-muted);
        font-size: var(--dees-font-caption2-size, 10px);
        line-height: 1.4;
      }

      .editorMeta {
        margin-top: 5px;
      }

      .actions {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: var(--dees-spacing-xs);
        margin-top: var(--dees-spacing-sm);
      }

      .conflict,
      .error {
        margin-top: var(--dees-spacing-sm);
        padding: var(--dees-spacing-xs) var(--dees-spacing-sm);
        border-radius: var(--dees-radius-md);
        font-size: var(--dees-font-caption1-size, 11px);
        line-height: 1.4;
      }

      .conflict {
        color: var(--dees-color-text-warning);
        background: var(--dees-color-badge-warning-bg);
      }

      .error {
        color: var(--dees-color-text-error);
        background: var(--dees-color-badge-error-bg);
      }

      .exchangeList {
        display: grid;
        gap: var(--dees-spacing-sm);
        margin-bottom: var(--dees-spacing-sm);
      }

      .exchange {
        display: grid;
        gap: 4px;
        padding-left: var(--dees-spacing-sm);
        border-left: 1px solid var(--dees-color-border-default);
      }

      .question,
      .answer {
        overflow-wrap: anywhere;
        font-size: var(--dees-font-caption1-size, 11px);
        line-height: 1.45;
        white-space: pre-wrap;
      }

      .question {
        color: var(--dees-color-text-secondary);
      }

      .answer {
        color: var(--dees-color-text-primary);
      }

      .exchange.running .answer {
        color: var(--dees-color-text-muted);
      }

      .exchange.error .answer {
        color: var(--dees-color-text-error);
      }

      .empty {
        color: var(--dees-color-text-muted);
        font-size: var(--dees-font-caption1-size, 11px);
      }

      @media (prefers-reduced-motion: reduce) {
        .contextProgressFill {
          transition: none;
        }
      }
    `,
  ];

  protected willUpdate(changedPropertiesArg: Map<PropertyKey, unknown>): void {
    this.captureActivityPositions(changedPropertiesArg);
    const availableSections = this.availableSections;
    if (!this.sectionStateInitialized && availableSections.length > 0) {
      this.expandedSection = this.defaultExpandedSection;
      this.sectionStateInitialized = true;
    } else if (
      this.expandedSection !== undefined
      && !availableSections.includes(this.expandedSection)
    ) {
      this.expandedSection = this.defaultExpandedSection;
    }
    if (changedPropertiesArg.has('sessionKey')) this.intelligenceQuestion = '';
    if (!changedPropertiesArg.has('scratchpad')) return;
    const next = this.scratchpad;
    if (!next) {
      this.scratchpadDraft = '';
      this.scratchpadDraftRevision = 0;
      this.scratchpadDirty = false;
      this.scratchpadStale = false;
      return;
    }
    const previous = changedPropertiesArg.get('scratchpad') as IHarnessScratchpad | undefined;
    if (!previous || previous.id !== next.id) {
      this.acceptScratchpad(next);
      return;
    }
    if (!this.scratchpadDirty || next.text === this.scratchpadDraft) {
      this.acceptScratchpad(next);
      return;
    }
    if (next.revision > this.scratchpadDraftRevision) this.scratchpadStale = true;
  }

  /**
   * Where every entry and group title sits before this update, measured against the list so the
   * section's own scrolling cannot skew it. A rect already includes a glide that is still running,
   * so an update arriving mid-flight continues from what the reader currently sees. A new session
   * or a section opening is a new list rather than a list that moved, and neither is measured.
   */
  private captureActivityPositions(changedPropertiesArg: Map<PropertyKey, unknown>): void {
    this.activityPositions.clear();
    this.activityFlipReady = false;
    const list = this.shadowRoot?.querySelector<HTMLElement>('.activityList');
    if (
      !list
      || changedPropertiesArg.has('sessionKey')
      || changedPropertiesArg.has('expandedSection')
    ) return;
    const origin = list.getBoundingClientRect().top;
    for (const element of this.activityFlipElements(list)) {
      const key = element.dataset.toolKey ?? element.dataset.activityKey;
      if (key) this.activityPositions.set(key, element.getBoundingClientRect().top - origin);
    }
    this.activityFlipReady = true;
  }

  /**
   * The second half of that measurement, in three passes so the browser resolves style once for
   * the whole list rather than once per entry: read where everything landed, offset the movers
   * back to where they were with their transition suppressed, then release them all so the
   * declared transition plays them home. Only entries whose old or new place is within the
   * section's viewport are worth animating — an entry that travels entirely off-screen in a long
   * history just takes its new place — and a key the list has not shown before blends in after the
   * glide. Nothing is left behind to clean up, and reduced motion gets none of it.
   */
  private playActivityFlip(): void {
    if (!this.activityFlipReady) return;
    this.activityFlipReady = false;
    const list = this.shadowRoot?.querySelector<HTMLElement>('.activityList');
    if (!list || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const viewport = list.closest<HTMLElement>('.sectionContent');
    const origin = list.getBoundingClientRect().top;
    const viewportTop = viewport ? viewport.getBoundingClientRect().top - origin : Number.NEGATIVE_INFINITY;
    const viewportBottom = viewport ? viewportTop + viewport.clientHeight : Number.POSITIVE_INFINITY;
    const movers: Array<{ element: HTMLElement; offset: number }> = [];
    const newcomers: HTMLElement[] = [];
    for (const element of this.activityFlipElements(list)) {
      const key = element.dataset.toolKey ?? element.dataset.activityKey;
      if (!key) continue;
      const previous = this.activityPositions.get(key);
      if (previous === undefined) {
        newcomers.push(element);
        continue;
      }
      const rect = element.getBoundingClientRect();
      const current = rect.top - origin;
      const offset = previous - current;
      if (Math.abs(offset) < 0.5) continue;
      const height = rect.height;
      const visible = (previous < viewportBottom && previous + height > viewportTop)
        || (current < viewportBottom && current + height > viewportTop);
      if (visible) movers.push({ element, offset });
    }
    for (const mover of movers) {
      mover.element.style.transition = 'none';
      mover.element.style.transform = `translateY(${mover.offset}px)`;
    }
    // One forced style resolution makes those offsets the starting point for every mover at once.
    if (movers.length) list.getBoundingClientRect();
    for (const mover of movers) {
      mover.element.style.transition = '';
      mover.element.style.transform = '';
    }
    for (const element of newcomers) {
      element.setAttribute('data-activity-entering', '');
      element.addEventListener(
        'animationend',
        () => element.removeAttribute('data-activity-entering'),
        { once: true },
      );
    }
  }

  private activityFlipElements(listArg: HTMLElement): HTMLElement[] {
    return [...listArg.querySelectorAll<HTMLElement>('[data-tool-key], [data-activity-key]')];
  }

  public updated(changedPropertiesArg: Map<PropertyKey, unknown>): void {
    super.updated(changedPropertiesArg);
    this.playActivityFlip();
    if (
      this.toolInspectorPortal
      && changedPropertiesArg.has('sessionKey')
      && this.inspectedSessionKey !== this.sessionKey
    ) {
      this.closeToolInspector();
      return;
    }
    if (
      changedPropertiesArg.has('toolMessages')
      || changedPropertiesArg.has('toolRegistry')
    ) this.refreshToolInspector();
  }

  public hasSection(sectionArg: THarnessSessionSidebarSection): boolean {
    return this.availableSections.includes(sectionArg);
  }

  /** Expands and focuses an available section without reaching through the component's shadow DOM. */
  public async openSection(sectionArg: THarnessSessionSidebarSection): Promise<boolean> {
    await this.updateComplete;
    if (!this.hasSection(sectionArg)) return false;
    this.sectionStateInitialized = true;
    this.expandedSection = sectionArg;
    await this.updateComplete;
    this.shadowRoot
      ?.querySelector<HTMLButtonElement>(`.${sectionArg}Section .sectionHeading`)
      ?.focus();
    return true;
  }

  public async disconnectedCallback(): Promise<void> {
    this.closeToolInspector(false);
    await super.disconnectedCallback();
  }

  public get contextPercentage(): number | undefined {
    const current = this.metrics.currentContextTokens;
    const maximum = this.metrics.maxContextTokens;
    if (current === undefined || maximum === undefined || maximum <= 0) return undefined;
    return Math.min(100, Math.max(0, (current / maximum) * 100));
  }

  public render(): TemplateResult {
    return html`
      <div class="sidebar">
        ${this.showTasks ? this.renderTasks() : ''}
        ${this.toolActivityEntries.length ? this.renderToolActivity() : ''}
        ${this.hasMetrics ? this.renderMetrics() : ''}
        ${this.scratchpad ? this.renderScratchpad() : ''}
        ${this.intelligenceEnabled ? this.renderIntelligence() : ''}
      </div>
    `;
  }

  private get hasMetrics(): boolean {
    return Object.values(this.metrics).some((value) => value !== undefined);
  }

  private get toolActivityEntries(): IToolActivityEntry[] {
    return this.toolMessages
      .filter((message): message is IHarnessMessage & { toolCall: IHarnessToolCall } => Boolean(message.toolCall))
      .map((message) => ({
        key: JSON.stringify([message.id, message.toolCall.id]),
        messageId: message.id,
        call: message.toolCall,
        createdAt: message.createdAt ?? message.toolCall.startedAt,
        status: harnessEffectiveToolStatus(message.toolCall),
      }))
      .sort((left, right) => (right.createdAt ?? 0) - (left.createdAt ?? 0));
  }

  private get availableSections(): THarnessSessionSidebarSection[] {
    return [
      ...(this.showTasks ? ['tasks' as const] : []),
      ...(this.toolActivityEntries.length ? ['activity' as const] : []),
      ...(this.hasMetrics ? ['metrics' as const] : []),
      ...(this.scratchpad ? ['scratchpad' as const] : []),
      ...(this.intelligenceEnabled ? ['intelligence' as const] : []),
    ];
  }

  private get defaultExpandedSection(): THarnessSessionSidebarSection | undefined {
    if (this.showTasks && this.todos.length > 0) return 'tasks';
    if (this.toolActivityEntries.length) return 'activity';
    if (this.hasMetrics) return 'metrics';
    if (this.scratchpad) return 'scratchpad';
    if (this.intelligenceEnabled) return 'intelligence';
    return this.showTasks ? 'tasks' : undefined;
  }

  private renderSection(
    idArg: THarnessSessionSidebarSection,
    labelArg: string,
    iconArg: string,
    contentArg: TemplateResult,
    summaryArg = '',
  ): TemplateResult {
    const expanded = this.expandedSection === idArg;
    const contentId = `harness-session-section-${idArg}`;
    return html`
      <section class=${`section ${idArg}Section ${expanded ? 'expanded' : ''}`}>
        <button
          class="sectionHeading"
          type="button"
          aria-expanded=${String(expanded)}
          aria-controls=${contentId}
          @click=${() => {
            this.sectionStateInitialized = true;
            this.expandedSection = expanded ? undefined : idArg;
          }}
        >
          <dees-icon
            icon=${expanded ? 'lucide:ChevronDown' : 'lucide:ChevronRight'}
            iconSize="13"
          ></dees-icon>
          <dees-icon icon=${iconArg} iconSize="13"></dees-icon>
          <span class="sectionHeadingText">${labelArg}</span>
          ${summaryArg ? html`<span class="sectionSummary">${summaryArg}</span>` : ''}
        </button>
        <div
          id=${contentId}
          class="sectionContent"
          role="region"
          aria-hidden=${String(!expanded)}
          ?hidden=${!expanded}
        >
          ${expanded ? contentArg : ''}
        </div>
      </section>
    `;
  }

  private renderTasks(): TemplateResult {
    const completed = this.todos.filter((todo) => todo.status === 'completed').length;
    return this.renderSection(
      'tasks',
      'Tasks',
      'lucide:ListChecks',
      html`<dees-harness-todos .todos=${this.todos} .showProgress=${false}></dees-harness-todos>`,
      this.todos.length > 0 ? `${completed}/${this.todos.length}` : '',
    );
  }

  private renderToolActivity(): TemplateResult {
    const entries = this.toolActivityEntries;
    const active = entries.filter((entry) => entry.status === 'pending' || entry.status === 'running');
    const history = entries.filter((entry) => entry.status === 'completed' || entry.status === 'error' || entry.status === 'stopped');
    const summary = [
      active.length ? `${active.length} active` : '',
      history.length || !active.length ? `${history.length} past` : '',
    ].filter(Boolean).join(' · ');
    return this.renderSection(
      'activity',
      'Tools & Subagents',
      'lucide:Wrench',
      html`
        <div class="activityList">
          ${active.length ? this.renderActivityGroup('Active', active) : ''}
          ${history.length ? this.renderActivityGroup('History', history) : ''}
        </div>
      `,
      summary,
    );
  }

  private renderActivityGroup(labelArg: string, entriesArg: IToolActivityEntry[]): TemplateResult {
    return html`
      <section class="activityGroup" aria-label=${labelArg}>
        <div class="activityGroupTitle" data-activity-key=${`title:${labelArg}`}>${labelArg}</div>
        ${repeat(
          entriesArg,
          (entry) => entry.key,
          (entry) => this.renderActivityEntry(entry),
        )}
      </section>
    `;
  }

  private renderActivityEntry(entryArg: IToolActivityEntry): TemplateResult {
    const descriptor = this.toolRegistry.resolve(entryArg.call);
    const label = typeof descriptor.label === 'function'
      ? descriptor.label(entryArg.call)
      : descriptor.label;
    const subtitle = descriptor.subtitle?.(entryArg.call) ?? '';
    const statusPresentation = harnessToolStatusPresentation(
      entryArg.call,
      descriptor.kind === 'terminal',
    );
    const relativeTime = harnessFormatRelativeTime(entryArg.createdAt);
    return html`
      <button
        class=${`activityItem status-${statusPresentation.status}`}
        type="button"
        data-tool-key=${entryArg.key}
        data-tool-message-id=${entryArg.messageId}
        data-tool-call-id=${entryArg.call.id}
        aria-label=${`Inspect ${label}`}
        @click=${(event: MouseEvent) => this.openToolInspector(entryArg, event.currentTarget as HTMLElement)}
      >
        <span class="activityIcon">
          <dees-icon icon=${descriptor.icon} iconSize="13"></dees-icon>
        </span>
        <span class="activityCopy">
          <span class="activityTitleRow">
            <span class="activityTitle" title=${label}>${label}</span>
            <span class="activityStatusPair">
              ${statusPresentation.exitCode === undefined
                ? ''
                : html`<span class="activityExitCode">Exit Code: ${statusPresentation.exitCode}</span>`}
              <span class="activityStatus">${statusPresentation.label}</span>
              <span class="activityInspect" aria-hidden="true">
                <dees-icon icon="lucide:SquareArrowOutUpRight" iconSize="11"></dees-icon>
              </span>
            </span>
          </span>
          ${subtitle
            ? html`<dees-harness-overflow-text class="activitySubtitle" title=${subtitle}>${subtitle}</dees-harness-overflow-text>`
            : ''}
          ${relativeTime ? html`<span class="activityMeta">${relativeTime}</span>` : ''}
        </span>
      </button>
    `;
  }

  private openToolInspector(entryArg: IToolActivityEntry, triggerArg: HTMLElement): void {
    this.closeToolInspector(false);
    const portal = document.createElement('dees-harness-tool-fullscreen');
    portal.call = entryArg.call;
    portal.toolRegistry = this.toolRegistry;
    portal.addEventListener('tool-fullscreen-close', this.handleToolInspectorClose, { once: true });
    portal.addEventListener('harness-subtask-open', this.bridgeSubtaskOpen);
    document.body.append(portal);
    this.toolInspectorPortal = portal;
    this.inspectedToolKey = entryArg.key;
    this.inspectedSessionKey = this.sessionKey;
    this.toolInspectorTrigger = triggerArg;
  }

  private refreshToolInspector(): void {
    const portal = this.toolInspectorPortal;
    if (!portal || !this.inspectedToolKey) return;
    const entry = this.toolActivityEntries.find((candidate) => candidate.key === this.inspectedToolKey);
    if (!entry || this.inspectedSessionKey !== this.sessionKey) {
      this.closeToolInspector();
      return;
    }
    portal.call = entry.call;
    portal.toolRegistry = this.toolRegistry;
    portal.refreshCall();
  }

  private readonly handleToolInspectorClose = (): void => {
    this.closeToolInspector();
  };

  private readonly bridgeSubtaskOpen = (event: Event): void => {
    event.stopPropagation();
    const detail = (event as CustomEvent<IHarnessSubtaskOpenDetail>).detail;
    this.dispatchEvent(new CustomEvent<IHarnessSubtaskOpenDetail>('harness-subtask-open', {
      detail,
      bubbles: true,
      composed: true,
    }));
  };

  private closeToolInspector(restoreFocus = true): void {
    const portal = this.toolInspectorPortal;
    const trigger = this.toolInspectorTrigger;
    const inspectedToolKey = this.inspectedToolKey;
    this.toolInspectorPortal = undefined;
    this.inspectedToolKey = undefined;
    this.inspectedSessionKey = undefined;
    this.toolInspectorTrigger = undefined;
    if (portal) {
      portal.removeEventListener('tool-fullscreen-close', this.handleToolInspectorClose);
      portal.removeEventListener('harness-subtask-open', this.bridgeSubtaskOpen);
      portal.remove();
    }
    if (restoreFocus && portal) {
      const currentTrigger = trigger?.isConnected
        ? trigger
        : inspectedToolKey
          ? Array.from(this.shadowRoot?.querySelectorAll<HTMLElement>('[data-tool-key]') ?? [])
              .find((candidate) => candidate.dataset.toolKey === inspectedToolKey)
          : undefined;
      (currentTrigger
        ?? this.shadowRoot?.querySelector<HTMLElement>('.activitySection .sectionHeading')
        ?? this.shadowRoot?.querySelector<HTMLElement>('.sectionHeading'))?.focus();
    }
  }

  private renderMetrics(): TemplateResult {
    const metrics = this.metrics;
    const percentage = this.contextPercentage;
    const rows: Array<[string, number | undefined]> = [
      ['Lifetime used', metrics.lifetimeUsedTokens],
      ['Compactions', metrics.compactionCount],
      ['Before latest compaction', metrics.tokensBeforeLatestCompaction],
      ['Context window', metrics.maxContextTokens],
      ['Current context', metrics.currentContextTokens],
    ];
    return this.renderSection(
      'metrics',
      'Tokens',
      'lucide:Gauge',
      html`
        <div class="metricList">
          ${rows.map(([label, value]) => value === undefined ? '' : html`
            <div class="metricRow">
              <span>${label}</span>
              <span class="metricValue">
                ${formatTokenCount(value)}${label === 'Current context' && percentage !== undefined
                  ? ` · ${Math.round(percentage)}%`
                  : ''}
              </span>
            </div>
          `)}
          ${percentage === undefined ? '' : html`
            <div
              class="contextProgress"
              role="progressbar"
              aria-label="Current context window usage"
              aria-valuemin="0"
              aria-valuemax="100"
              aria-valuenow=${String(Math.round(percentage))}
            >
              <div class="contextProgressFill" style=${`width: ${percentage}%`}></div>
            </div>
          `}
        </div>
      `,
    );
  }

  private renderScratchpad(): TemplateResult {
    const scratchpad = this.scratchpad!;
    return this.renderSection(
      'scratchpad',
      'Session Scratchpad',
      'lucide:NotebookPen',
      html`
        <textarea
          aria-label="Session scratchpad"
          maxlength="32768"
          .value=${this.scratchpadDraft}
          ?disabled=${this.disabled || this.scratchpadBusy}
          @input=${(event: InputEvent) => {
            this.scratchpadDraft = (event.currentTarget as HTMLTextAreaElement).value;
            this.scratchpadDirty = this.scratchpadDraft !== scratchpad.text;
          }}
          @keydown=${(event: KeyboardEvent) => {
            if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
              event.preventDefault();
              this.saveScratchpad();
            }
          }}
        ></textarea>
        ${this.scratchpadStale ? html`
          <div class="conflict" role="status">
            A newer revision arrived. Keep this draft on the latest revision or discard it.
            <div class="actions">
              <dees-button
                size="sm"
                type="ghost"
                text="Discard draft"
                @clicked=${this.discardScratchpadDraft}
              ></dees-button>
              <dees-button
                size="sm"
                type="outline"
                text="Keep draft on latest"
                @clicked=${this.rebaseScratchpadDraft}
              ></dees-button>
            </div>
          </div>
        ` : ''}
        ${this.scratchpadError ? html`<div class="error" role="alert">${this.scratchpadError}</div>` : ''}
        <div class="scratchpadFooter">
          <span class="editorMeta">rev. ${scratchpad.revision}</span>
          <dees-button
            size="sm"
            type="accent"
            text="Save"
            .status=${this.scratchpadBusy ? 'pending' : 'normal'}
            ?disabled=${this.disabled || this.scratchpadBusy || !this.scratchpadDirty || this.scratchpadStale}
            @clicked=${this.saveScratchpad}
          ></dees-button>
        </div>
      `,
    );
  }

  private renderIntelligence(): TemplateResult {
    const heading = this.intelligenceHeading.trim() || 'Session Intelligence';
    const intelligenceAvailable = this.intelligenceAvailabilityStatus === 'available';
    const availabilityMessage = this.intelligenceAvailabilityStatus === 'checking'
      ? `Checking ${heading} availability…`
      : this.intelligenceAvailabilityStatus === 'unavailable'
        ? this.intelligenceUnavailableReason.trim() || `${heading} is unavailable.`
        : '';
    return this.renderSection(
      'intelligence',
      heading,
      'lucide:Sparkles',
      html`
        <div class="intelligenceHistory" role="log" aria-live="polite">
          ${this.intelligenceExchanges.length ? html`
            <div class="exchangeList">
              ${repeat(
                this.intelligenceExchanges,
                (exchange) => exchange.id,
                (exchange) => this.renderExchange(exchange),
              )}
            </div>
          ` : html`<div class="empty">Ask about this session without interrupting its main work.</div>`}
        </div>
        <div class="intelligenceComposer">
          <div
            class="intelligenceAvailabilityStatus"
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >${availabilityMessage
            ? html`<div class="intelligenceAvailability">${availabilityMessage}</div>`
            : ''}</div>
          <textarea
            class="intelligenceInput"
            aria-label=${`Ask ${heading}`}
            placeholder="Ask a parallel question…"
            maxlength="8192"
            rows="2"
            .value=${this.intelligenceQuestion}
            ?disabled=${this.disabled || this.intelligenceBusy || !intelligenceAvailable}
            @input=${(event: InputEvent) => {
              this.intelligenceQuestion = (event.currentTarget as HTMLTextAreaElement).value;
            }}
            @keydown=${(event: KeyboardEvent) => {
              if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                this.askIntelligence();
              }
            }}
          ></textarea>
          <div class="intelligenceControls">
            <span class="hint">Parallel session · Ctrl/⌘ + Enter</span>
            <dees-button
              size="sm"
              type="accent"
              text="Ask"
              .status=${this.intelligenceBusy ? 'pending' : 'normal'}
              ?disabled=${this.disabled
                || this.intelligenceBusy
                || !intelligenceAvailable
                || !this.intelligenceQuestion.trim()}
              @clicked=${this.askIntelligence}
            ></dees-button>
          </div>
          ${this.intelligenceError ? html`<div class="error" role="alert">${this.intelligenceError}</div>` : ''}
        </div>
      `,
    );
  }

  private renderExchange(exchangeArg: IHarnessIntelligenceExchange): TemplateResult {
    const heading = this.intelligenceHeading.trim() || 'Session Intelligence';
    const answer = exchangeArg.status === 'running'
      ? 'Thinking in parallel…'
      : exchangeArg.status === 'error'
        ? exchangeArg.error || `${heading} failed.`
        : exchangeArg.answer || '';
    return html`
      <article class="exchange ${exchangeArg.status}">
        <div class="question">${exchangeArg.question}</div>
        <div class="answer">${answer}</div>
        ${exchangeArg.model
          ? html`<div class="exchangeMeta">${exchangeArg.model}</div>`
          : ''}
      </article>
    `;
  }

  private acceptScratchpad(scratchpadArg: IHarnessScratchpad): void {
    this.scratchpadDraft = scratchpadArg.text;
    this.scratchpadDraftRevision = scratchpadArg.revision;
    this.scratchpadDirty = false;
    this.scratchpadStale = false;
  }

  private readonly discardScratchpadDraft = (): void => {
    if (this.scratchpad) this.acceptScratchpad(this.scratchpad);
  };

  private readonly rebaseScratchpadDraft = (): void => {
    if (!this.scratchpad) return;
    this.scratchpadDraftRevision = this.scratchpad.revision;
    this.scratchpadStale = false;
    this.scratchpadDirty = this.scratchpadDraft !== this.scratchpad.text;
  };

  private readonly saveScratchpad = (): void => {
    if (
      !this.scratchpad
      || !this.scratchpadDirty
      || this.scratchpadStale
      || this.disabled
      || this.scratchpadBusy
    ) return;
    const detail: IHarnessScratchpadSaveDetail = {
      text: this.scratchpadDraft,
      expectedRevision: this.scratchpadDraftRevision,
    };
    this.dispatchEvent(new CustomEvent('harness-scratchpad-save', {
      detail,
      bubbles: true,
      composed: true,
    }));
  };

  private readonly askIntelligence = (): void => {
    const question = this.intelligenceQuestion.trim();
    if (
      !question
      || !this.intelligenceEnabled
      || this.intelligenceAvailabilityStatus !== 'available'
      || this.disabled
      || this.intelligenceBusy
    ) return;
    const detail: IHarnessSessionIntelligenceAskDetail = { question };
    this.dispatchEvent(new CustomEvent('harness-session-intelligence-ask', {
      detail,
      bubbles: true,
      composed: true,
    }));
    this.intelligenceQuestion = '';
  };
}
